import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import { workflowStore } from '@/stores/WorkflowStore'
import { workspaceStore } from '@/stores/WorkspaceStore'
import WorkflowCanvas from '@/components/workflow/WorkflowCanvas'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { WorkflowStatus } from '@/types'

const WorkflowEditorPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<WorkflowStatus>(WorkflowStatus.DRAFT)

  const isNewWorkflow = !id || id === 'new'

  useEffect(() => {
    const initWorkflow = async () => {
      setIsLoading(true)
      try {
        if (isNewWorkflow) {
          // Create a new workflow locally
          workflowStore.clearCanvas()
          workflowStore.selectedWorkflow = null
          setName('Untitled Workflow')
          setDescription('')
          setStatus(WorkflowStatus.DRAFT)
        } else {
          // Load existing workflow
          const workflow = await workflowStore.fetchWorkflowById(id)
          if (workflow) {
            setName(workflow.name)
            setDescription(workflow.description || '')
            setStatus(workflow.status)
            workflowStore.loadWorkflow(workflow)
          } else {
            toast.error('Workflow not found')
            navigate('/workflows')
          }
        }
      } catch (error) {
        toast.error('Failed to load workflow')
        navigate('/workflows')
      } finally {
        setIsLoading(false)
      }
    }

    initWorkflow()
  }, [id, isNewWorkflow, navigate])

  const handleSaveSettings = async () => {
    try {
      if (isNewWorkflow) {
        // Create new workflow
        const workflow = await workflowStore.createWorkflow({
          workspace_id: workspaceStore.currentWorkspace?.id,
          name,
          description,
          status,
          framework: 'langchain',
          definition: {
            nodes: workflowStore.nodes,
            edges: workflowStore.edges,
          },
        })
        toast.success('Workflow created successfully!')
        navigate(`/workflows/${workflow.id}`)
      } else {
        // Update existing workflow
        await workflowStore.updateWorkflow(id, {
          name,
          description,
          status,
          framework: 'langchain',
          definition: {
            nodes: workflowStore.nodes,
            edges: workflowStore.edges,
          },
        })
        toast.success('Workflow updated successfully!')
        setShowSettings(false)
      }
    } catch (error) {
      toast.error('Failed to save workflow')
    }
  }

  const handleBack = () => {
    if (workflowStore.isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/workflows')
      }
    } else {
      navigate('/workflows')
    }
  }

  const getStatusBadgeVariant = (status: WorkflowStatus) => {
    const variants: Record<WorkflowStatus, 'primary' | 'success' | 'warning' | 'gray'> = {
      [WorkflowStatus.DRAFT]: 'gray',
      [WorkflowStatus.ACTIVE]: 'success',
      [WorkflowStatus.ARCHIVED]: 'warning',
    }
    return variants[status]
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading workflow...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Workflow Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Workflow Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter workflow name"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter workflow description"
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as WorkflowStatus)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value={WorkflowStatus.DRAFT}>Draft</option>
                  <option value={WorkflowStatus.ACTIVE}>Active</option>
                  <option value={WorkflowStatus.ARCHIVED}>Archived</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Only active workflows can be executed
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-600 mb-2">Workflow Stats</h4>
                <div className="space-y-2 text-sm text-blue-600">
                  <div className="flex justify-between">
                    <span>Nodes:</span>
                    <span className="font-medium">{workflowStore.nodes.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Connections:</span>
                    <span className="font-medium">{workflowStore.edges.length}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveSettings}
                disabled={!name.trim()}
              >
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                {isNewWorkflow ? 'Create Workflow' : 'Save Settings'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">{name}</h1>
                <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
                {workflowStore.isDirty && (
                  <span className="text-xs text-orange-600 font-medium">• Unsaved</span>
                )}
              </div>
              {description && (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              )}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowSettings(true)}>
            Settings
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-hidden">
        <WorkflowCanvas workflowId={id} />
      </div>
    </div>
  )
})

export default WorkflowEditorPage
